const AUDIT_HOST = process.env.NEXT_PUBLIC_UBA_AUDIT_URL ?? '';
const API_ENDPOINT = `${AUDIT_HOST}/api/meta-pixel-audit`;
const APP_NAME = process.env.NEXT_PUBLIC_APP_NAME ?? '';

const LOG_TAG = 'META_PIXEL_AUDIT';
const FLUSH_INTERVAL_MS = 5000;
const DEFAULT_MAX_BUFFER_SIZE = 1;
const RETRY_BUFFER_MULTIPLIER = 5;
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
const MAX_PAYLOAD_CHARS = 4000;

const PERSONAL_MATCH_KEYS = [
  'em',
  'ph',
  'fn',
  'ln',
  'ct',
  'db',
  'external_id',
] as const;
const PERSONAL_KEYS: ReadonlySet<string> = new Set(PERSONAL_MATCH_KEYS);

const META_GTM_TAGS: Record<string, {hirist?: string; iimjobs?: string}> = {
  ClickApply: {hirist: 'Meta - Apply Click', iimjobs: 'Meta - ClickApply'},
  SuccessApply: {
    hirist: 'Meta - Apply Success',
    iimjobs: 'Meta - SuccessApply',
  },
  ViewJobDescription: {
    hirist: 'Meta - JD View',
    iimjobs: 'Meta - ViewJobDescription',
  },
  jsSaveAdditionalInfo: {hirist: 'Meta - Custom Mod'},
  premium_sal_15: {hirist: 'Meta_premium_cv_15L'},
  premium_sal_25: {hirist: 'Meta_premium_cv_25L'},
  premium_sal_30: {hirist: 'Meta_premium_cv_30L'},
  jsUploadResume: {
    hirist: 'Meta Tag - Reg (With CV)',
    iimjobs: 'Meta Tag - Reg (With CV)',
  },
  register_portable: {hirist: 'Meta Tag - Reg (Without CV)'},
};

export type MetaPixelLogStatus =
  | 'fired'
  | 'fbq-unavailable'
  | 'error'
  | 'gtm-pushed';

export type MetaPixelLogSource = 'fbq' | 'gtm';

export type MetaPixelTagLookup = 'tag-map' | 'match-params';

export interface MetaPixelLogContext {
  status: MetaPixelLogStatus;
  source: MetaPixelLogSource;
  fbqAvailable?: boolean;
  metaTag?: string;
  taggedBy?: MetaPixelTagLookup;
  dataLayerEvent?: string;
  error?: unknown;
}

interface MetaPixelLoggingCriteria {
  enableMetaPixelLogging?: boolean;
  eventNames?: string[];
  sampleRate?: number;
  maxBufferSize?: number;
  maskPersonalParams?: boolean;
}

type PixelWindow = {
  fbq?: unknown;
  google_tag_manager?: unknown;
  __T_DS__?: string;
  __FEATURE_FLAGS__?: {
    metaJobEvents?: boolean;
    metaPixelLoggingCriteria?: MetaPixelLoggingCriteria;
  };
};

const safely = <T>(read: () => T, fallback: T): T => {
  try {
    return read();
  } catch {
    return fallback;
  }
};

const pixelWindow = (): PixelWindow => window as unknown as PixelWindow;

const istISOString = (): string =>
  new Date(Date.now() + IST_OFFSET_MS).toISOString().replace('Z', '+05:30');

const generatePixelLogId = (): string =>
  `MP-${Date.now()}-${Math.random().toString(36).slice(2, 7).toUpperCase()}`;

const getConnectionType = (): string =>
  safely(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const conn = (navigator as any).connection;
    return String(conn?.effectiveType ?? conn?.type ?? 'unknown');
  }, 'unknown');

const getTds = (): string => safely(() => pixelWindow().__T_DS__ ?? '', '');

const getDeviceOs = (userAgent: string): string => {
  const isMobile =
    /(iPad|iPhone|Android|webOS|iPod|BlackBerry|IEMobile|Opera Mini|Mobile)/i.test(
      userAgent
    );
  if (!isMobile) return 'web';
  if (/android/i.test(userAgent)) return 'android';
  return /iphone|ipad|ipod|ios/i.test(userAgent) ? 'ios' : 'web';
};

const readErrorMessage = (error?: unknown): string => {
  if (error === undefined || error === null) return '';
  if (error instanceof Error) {
    const name = error.name || 'Error';
    return error.message ? `${name}: ${error.message}` : name;
  }
  if (typeof error === 'string') return error || 'empty-error-string';
  return safely(
    () => `${typeof error}: ${JSON.stringify(error)}`,
    `${typeof error}: unserializable`
  );
};

const describeDeliveryGap = (
  source: MetaPixelLogSource,
  fbqAvailable: boolean,
  gtmLoaded: boolean
): string => {
  if (source === 'fbq') {
    return fbqAvailable
      ? ''
      : 'fbq unavailable: Meta base pixel script had not loaded';
  }
  if (!gtmLoaded) {
    return `GTM container${fbqAvailable ? '' : ' and Meta base pixel'} had not loaded; the dataLayer push was never read`;
  }
  return fbqAvailable
    ? ''
    : 'Meta base pixel script had not loaded when the tag fired';
};

const maskPersonalValue = (value: unknown): unknown => {
  if (typeof value === 'string') return value ? `***:${value.length}` : '';
  if (value === null || value === undefined) return value;
  return `***:${String(value).length}`;
};

const maskIf = (mask: boolean, key: string, value: unknown): unknown =>
  mask && PERSONAL_KEYS.has(key) ? maskPersonalValue(value) : value;

const clonePayload = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params: Record<string, any>,
  mask: boolean
): {payload: Record<string, unknown>; truncated: boolean} => {
  if (!params || typeof params !== 'object')
    return {payload: {}, truncated: false};

  const seen = new WeakSet<object>();
  const replacer = (key: string, value: unknown): unknown => {
    if (typeof value === 'function' || typeof value === 'symbol')
      return undefined;
    if (typeof value === 'bigint') return String(value);
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) return '[circular]';
      seen.add(value);
    }
    return maskIf(mask, key, value);
  };

  try {
    const json = JSON.stringify(params, replacer);
    if (!json) return {payload: {}, truncated: false};
    if (json.length <= MAX_PAYLOAD_CHARS)
      return {payload: JSON.parse(json), truncated: false};

    const scalars: Record<string, unknown> = {};
    Object.entries(params).forEach(([key, value]) => {
      if (value === null || typeof value !== 'object')
        scalars[key] = maskIf(mask, key, value);
    });
    return {payload: scalars, truncated: true};
  } catch {
    return {payload: {}, truncated: true};
  }
};

const toStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.map(String);
  if (value === undefined || value === null || value === '') return [];
  return [String(value)];
};

const isPopulated = (value: unknown): boolean =>
  typeof value === 'string' ? value.length > 0 : value != null;

const hasMetaMatchParams = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params: Record<string, any>
): boolean =>
  ['em', 'ph'].some(key => {
    const value = params?.[key];
    return typeof value === 'string' && value.length > 0;
  });

const isMetaJobEventsEnabled = (): boolean =>
  safely(() => !!pixelWindow().__FEATURE_FLAGS__?.metaJobEvents, false);

const isFbqAvailable = (): boolean =>
  safely(() => typeof pixelWindow().fbq === 'function', false);

const isGtmLoaded = (): boolean =>
  safely(() => !!pixelWindow().google_tag_manager, false);

const metaGtmTagFor = (action: string): string => {
  if (!Object.prototype.hasOwnProperty.call(META_GTM_TAGS, action)) return '';
  return META_GTM_TAGS[action]?.[APP_NAME as 'hirist' | 'iimjobs'] ?? '';
};

const buildEntry = (
  eventName: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params: Record<string, any>,
  context: MetaPixelLogContext,
  mask: boolean
) => {
  const userAgent = navigator.userAgent;
  const fbqAvailable = context.fbqAvailable ?? isFbqAvailable();
  const gtmLoaded = isGtmLoaded();
  const sent =
    context.source === 'gtm' && context.dataLayerEvent
      ? {event: context.dataLayerEvent, ...params}
      : params;
  const {payload, truncated} = clonePayload(sent, mask);
  const matchKeys = PERSONAL_MATCH_KEYS.filter(key =>
    isPopulated(params?.[key])
  );

  return {
    logTag: LOG_TAG,
    pixelLogId: generatePixelLogId(),
    timestamp: istISOString(),
    eventName,
    status: context.status,
    source: context.source,
    metaTag: context.metaTag ?? '',
    taggedBy: context.taggedBy ?? null,
    dataLayerEvent: context.dataLayerEvent ?? '',
    fbqAvailable,
    gtmLoaded,
    errorMessage:
      readErrorMessage(context.error) ||
      describeDeliveryGap(context.source, fbqAvailable, gtmLoaded),
    contentType: String(params?.content_type ?? ''),
    contentIds: toStringArray(params?.content_id),
    currency: String(params?.currency ?? ''),
    value: typeof params?.value === 'number' ? (params.value as number) : null,
    matchKeys: [...matchKeys],
    matchKeyCount: matchKeys.length,
    payload,
    payloadKeys: Object.keys(payload),
    payloadTruncated: truncated,
    country: String(params?.country ?? ''),
    fbp: String(params?.fbp ?? ''),
    fbc: String(params?.fbc ?? ''),
    appId: process.env.NEXT_PUBLIC_UBA_APP_ID ?? '',
    appName: APP_NAME,
    appVersion: process.env.NEXT_PUBLIC_VERSION ?? '',
    pageUrl: window.location.href,
    pagePath: window.location.pathname,
    referer: document.referrer ?? '',
    userid: sessionStorage.getItem('userId') ?? '',
    deviceType: getDeviceOs(userAgent),
    isMobile: /(iPad|iPhone|Android|Mobile)/i.test(userAgent),
    connectionType: getConnectionType(),
    isOnline: navigator.onLine,
    t_ds: getTds(),
  };
};

type MetaPixelLogEntry = ReturnType<typeof buildEntry>;

class MetaPixelAuditLogger {
  private buffer: MetaPixelLogEntry[] = [];
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private initialized = false;
  private criteria: MetaPixelLoggingCriteria | null = null;

  logEvent(
    eventName: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    params: Record<string, any>,
    context: MetaPixelLogContext
  ): void {
    if (typeof window === 'undefined' || !AUDIT_HOST) return;
    this.record(eventName, params, context);
  }

  logGtmEvent(
    action: string,
    dataLayerEvent: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    params: Record<string, any>
  ): void {
    if (typeof window === 'undefined' || !isMetaJobEventsEnabled()) return;

    const metaTag = metaGtmTagFor(action);
    const taggedBy: MetaPixelTagLookup | null = metaTag
      ? 'tag-map'
      : hasMetaMatchParams(params)
        ? 'match-params'
        : null;
    if (!taggedBy || !AUDIT_HOST) return;

    this.record(dataLayerEvent, params, {
      status: 'gtm-pushed',
      source: 'gtm',
      metaTag,
      taggedBy,
      dataLayerEvent,
      fbqAvailable: isFbqAvailable(),
    });
  }

  private record(
    eventName: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    params: Record<string, any>,
    context: MetaPixelLogContext
  ): void {
    try {
      if (!this.shouldLogEvent(eventName)) return;
      const mask = !!this.getCriteria()?.maskPersonalParams;
      this.queueEvent(buildEntry(eventName, params, context, mask));
    } catch {
      return;
    }
  }

  private getCriteria(): MetaPixelLoggingCriteria | null {
    if (this.criteria) return this.criteria;
    this.criteria = safely(
      () => pixelWindow().__FEATURE_FLAGS__?.metaPixelLoggingCriteria ?? null,
      null
    );
    return this.criteria;
  }

  private shouldLogEvent(eventName: string): boolean {
    const criteria = this.getCriteria();
    if (!criteria) return true;
    if (criteria.enableMetaPixelLogging === false) return false;

    const {eventNames, sampleRate} = criteria;
    if (eventNames?.length && !eventNames.includes(eventName)) return false;

    return !(
      typeof sampleRate === 'number' &&
      sampleRate < 1 &&
      Math.random() >= Math.max(sampleRate, 0)
    );
  }

  private init(): void {
    if (this.initialized || typeof window === 'undefined') return;
    this.initialized = true;

    this.flushTimer = setInterval(() => this.flush(), FLUSH_INTERVAL_MS);
    window.addEventListener('beforeunload', () => this.flushWithBeacon());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.flushWithBeacon();
    });
  }

  private get maxBufferSize(): number {
    return this.getCriteria()?.maxBufferSize || DEFAULT_MAX_BUFFER_SIZE;
  }

  private queueEvent(entry: MetaPixelLogEntry): void {
    this.init();
    this.buffer.push(entry);
    if (this.buffer.length >= this.maxBufferSize) this.flush();
  }

  private async flush(): Promise<void> {
    const events = this.buffer.splice(0);
    if (events.length === 0) return;

    try {
      await fetch(API_ENDPOINT, {
        method: 'POST',
        body: JSON.stringify({events}),
        keepalive: true,
        headers: {'Content-Type': 'text/plain'},
      });
    } catch {
      this.buffer.unshift(...events);
      const cap = this.maxBufferSize * RETRY_BUFFER_MULTIPLIER;
      if (this.buffer.length > cap) this.buffer = this.buffer.slice(-cap);
    }
  }

  private flushWithBeacon(): void {
    const events = this.buffer.splice(0);
    if (events.length === 0) return;
    safely(
      () => navigator.sendBeacon(API_ENDPOINT, JSON.stringify({events})),
      false
    );
  }

  destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    this.flush();
    this.initialized = false;
  }
}

const metaPixelAuditLogger = new MetaPixelAuditLogger();

export default metaPixelAuditLogger;
